import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    ChangeEvent,
} from "react";
import { useTranslation } from "Shared/util/custom-hooks";
import { listOpenBankingBanks } from "Shared/util/web-api";
import { AffiliatedBankResource, BankResource } from "Shared/util/types";
import useBrandContext from "Contexts/brand-context";
import { openImboxFaqArticle, toggleImbox } from "Shared/util/imbox-api";
import { joinClassNames as c } from "Shared/util/styles";
import Button from "Shared/components/button";
import { Select, SelectItem } from "Shared/components/select";
import InputText from "Shared/components/input-text";
import { RadioGroup, RadioGroupOption } from "Shared/components/radio-group";
import Spinner from "Shared/components/spinner";
import TextLink from "Shared/components/text-link";
import { isValidSwedishSocialSecurityNumber } from "Shared/util/social-security-number";
import styles from "./select-bank-tab.css";
import { SelectBankTabProps } from "./types";
import { radioGroupOptionMapper } from "./utils";

export const SelectBankTab = ({
    onBankChanged,
    onBankSelected,
    onError,
    preselectedAffiliatedBankId,
    preselectedBic,
    show,
}: SelectBankTabProps) => {
    const t = useTranslation((x) => x.edit_payment_information.select_bank_tab);
    const { brandData } = useBrandContext();

    const [banks, setBanks] = useState<BankResource[]>([]);
    const [loadingBanks, setLoadingBanks] = useState(true);
    const [selectedAffiliatedBank, setSelectedAffiliatedBank] = useState<
        AffiliatedBankResource | undefined
    >();
    const [selectedBank, setSelectedBank] = useState<BankResource>();
    const [socialSecurityNumber, setSocialSecurityNumber] = useState<
        string | undefined
    >();
    const [isValidSocialSecurityNumber, setIsValidSocialSecurityNumber] =
        useState(false);

    const bankRadioOptions = useMemo(
        (): RadioGroupOption[] =>
            preselectedBic
                ? [
                      radioGroupOptionMapper(
                          banks.find((b) => b.bic === preselectedBic),
                          t,
                      ),
                  ]
                : banks.map((bank) => radioGroupOptionMapper(bank, t)),
        [banks, preselectedBic, t],
    );

    const affiliatedBankSelectItems = useMemo(
        (): SelectItem<string>[] =>
            selectedBank?.affiliatedBanks.map(({ id, name }) => ({
                label: name,
                value: id,
            })) ?? [],
        [selectedBank],
    );

    useEffect(() => {
        listOpenBankingBanks()
            .then((result: BankResource[]) => setBanks(result))
            .catch(() => onError())
            .finally(() => setLoadingBanks(false));
    }, []);

    const handleBankChanged = useCallback(
        (bic: string) => {
            const bank = banks.find((x) => x.bic === bic);
            const affiliatedBank = bank?.affiliatedBanks?.find(
                (x) => x.id === preselectedAffiliatedBankId,
            );

            setSelectedAffiliatedBank(affiliatedBank || undefined);
            setSocialSecurityNumber(undefined);
            setIsValidSocialSecurityNumber(false);
            setSelectedBank(bank);
            onBankChanged(bic);
        },
        [banks, onBankChanged, preselectedAffiliatedBankId],
    );

    useEffect(() => {
        if (preselectedBic) {
            handleBankChanged(preselectedBic);
        }
    }, [handleBankChanged, preselectedBic]);

    const handleBankSelected = () => {
        if (!selectedBank) {
            throw new Error("Invalid bank selected");
        }

        onBankSelected(
            selectedBank,
            selectedAffiliatedBank,
            socialSecurityNumber,
        );
    };

    const handleAffiliatedBankChanged = (value: string) =>
        setSelectedAffiliatedBank(
            selectedBank?.affiliatedBanks?.find((x) => x.id === value),
        );

    const handleSocialSecurityNumberChanged = (
        _: ChangeEvent<HTMLInputElement>,
        formattedSwedishSSN: string | undefined = "",
    ) => {
        setSocialSecurityNumber(formattedSwedishSSN);
        setIsValidSocialSecurityNumber(
            isValidSwedishSocialSecurityNumber(formattedSwedishSSN),
        );
    };

    const handleOpenImbox = () => {
        const articleId = brandData?.imbox?.paymentInformationArticleId;
        if (articleId) {
            openImboxFaqArticle(articleId);
        } else {
            toggleImbox();
        }
    };

    const disableSelectBankButton =
        !selectedBank ||
        !selectedBank.bic ||
        (selectedBank.requireAffiliatedBank && !selectedAffiliatedBank) ||
        (selectedBank.requireSocialSecurityNumber &&
            !isValidSocialSecurityNumber);

    return !show ? null : (
        <div className={c(styles.container, show && styles.active)}>
            <h3>{t.retrieve_bank_accounts}</h3>
            <p className={styles.description}>{t.select_bank_description}</p>
            {loadingBanks ? (
                <Spinner />
            ) : (
                <>
                    <RadioGroup
                        onChange={handleBankChanged}
                        options={bankRadioOptions}
                        selectedOption={selectedBank?.bic || ""}
                    />
                    {selectedBank?.requireAffiliatedBank && (
                        <Select
                            className={styles.affiliatedBanksContainer}
                            disabled={!!preselectedAffiliatedBankId}
                            items={affiliatedBankSelectItems}
                            label={t.bank}
                            onChange={handleAffiliatedBankChanged}
                            placeholder={t.select_bank}
                            selectedValue={selectedAffiliatedBank?.id}
                        />
                    )}
                    {selectedBank?.requireSocialSecurityNumber && (
                        <InputText
                            // @ts-ignore: TODO: Fix error
                            labelClassName={styles.socialSecurityNumberInput}
                            inputType="socialSecurityNumber"
                            onChange={handleSocialSecurityNumberChanged}
                            value={socialSecurityNumber || ""}
                        />
                    )}
                    <Button
                        attributes={{ disabled: disableSelectBankButton }}
                        buttonType="action-primary"
                        className={styles.selectBankButton}
                        onClick={handleBankSelected}
                        text={t.continue}
                    />
                    <TextLink
                        className={styles.missingBankTextLink}
                        link={handleOpenImbox}
                        text={t.do_you_need_any_help}
                    />
                </>
            )}
        </div>
    );
};
